import React, { useState, useEffect, useRef } from 'react';
import {
  Network,
  Database,
  Cloud,
  Server,
  Smartphone,
  Shield,
  Loader2,
  Download,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Info,
  Eye,
  List
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ArchitectureDiagram as ArchitectureDiagramType, DataFlowMap, IntegrationComplexity } from '../../services/processOptimizationAPI';
import VisualArchitectureDiagram from './VisualArchitectureDiagram';
import VisualDataFlowDiagram from './VisualDataFlowDiagram';

interface ArchitectureDiagramProps {
  opportunityId: number;
  projectId: number;
  opportunityTitle: string;
}

const ArchitectureDiagram: React.FC<ArchitectureDiagramProps> = ({
  opportunityId,
  projectId,
  opportunityTitle
}) => {
  const [loading, setLoading] = useState(false);
  const [architecture, setArchitecture] = useState<ArchitectureDiagramType | null>(null);
  const [dataFlow, setDataFlow] = useState<DataFlowMap | null>(null);
  const [complexity, setComplexity] = useState<IntegrationComplexity | null>(null);
  const [activeTab, setActiveTab] = useState<'architecture' | 'dataflow'>('architecture');
  const [viewMode, setViewMode] = useState<'visual' | 'cards'>('visual');
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    complexity: true,
    security: false
  });
  const diagramRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadArchitecture();
  }, [opportunityId, projectId]);

  const loadArchitecture = async () => {
    try {
      setLoading(true);
      const response = await api.post(
        `/api/process-optimization/${projectId}/opportunities/${opportunityId}/architecture-diagram`
      );
      setArchitecture(response.data.data.architecture);
      setDataFlow(response.data.data.dataFlow);
      setComplexity(response.data.data.complexity);
    } catch (error) {
      console.error('Failed to load architecture:', error);
      toast.error('Failed to generate architecture diagram');
    } finally {
      setLoading(false);
    }
  };

  const getSystemIcon = (type: string) => {
    switch (type) {
      case 'database':
        return <Database className="w-5 h-5" />;
      case 'api':
        return <Cloud className="w-5 h-5" />;
      case 'ui':
        return <Smartphone className="w-5 h-5" />;
      case 'middleware':
        return <Server className="w-5 h-5" />;
      default:
        return <Network className="w-5 h-5" />;
    }
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'simple':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          border: 'border-green-300',
          badge: 'bg-green-600'
        };
      case 'moderate':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          border: 'border-yellow-300',
          badge: 'bg-yellow-600'
        };
      case 'complex':
        return {
          bg: 'bg-red-100',
          text: 'text-red-800',
          border: 'border-red-300',
          badge: 'bg-red-600'
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          border: 'border-gray-300',
          badge: 'bg-gray-600'
        };
    }
  };

  const exportDiagram = async (format: 'png' | 'pdf') => {
    if (!diagramRef.current) return;

    try {
      const canvas = await html2canvas(diagramRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });

      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `architecture-${opportunityId}.png`;
        link.href = canvas.toDataURL();
        link.click();
        toast.success('Diagram exported as PNG');
      } else {
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height]
        });
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save(`architecture-${opportunityId}.pdf`);
        toast.success('Diagram exported as PDF');
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export diagram');
    }
  };

  if (loading) {
    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          <span className="text-gray-600">Generating architecture diagram...</span>
        </div>
      </div>
    );
  }

  if (!architecture) {
    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg p-8">
        <div className="text-center text-gray-500">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>No architecture diagram available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-purple-200 rounded-xl overflow-hidden shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-b-2 border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600 rounded-lg">
              <Network className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Technical Architecture</h3>
              <p className="text-sm text-gray-600">System integration and data flow visualization</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => exportDiagram('png')}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              PNG
            </button>
            <button
              onClick={() => exportDiagram('pdf')}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              PDF
            </button>
          </div>
        </div>

        {/* Tabs and View Mode Toggle */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('architecture')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'architecture'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Architecture
            </button>
            <button
              onClick={() => setActiveTab('dataflow')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'dataflow'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Data Flow
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="flex gap-1 bg-white rounded-lg p-1 border border-gray-300">
            <button
              onClick={() => setViewMode('visual')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                viewMode === 'visual'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title="Visual Diagram View"
            >
              <Eye className="w-4 h-4" />
              Visual
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                viewMode === 'cards'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title="Card List View"
            >
              <List className="w-4 h-4" />
              Cards
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6" ref={diagramRef}>
        {/* Integration Complexity Summary */}
        {complexity && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4">
            <button
              onClick={() => setExpandedSections(prev => ({ ...prev, complexity: !prev.complexity }))}
              className="w-full flex items-center justify-between"
            >
              <h4 className="font-bold text-gray-900 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                Integration Complexity Summary
              </h4>
              {expandedSections.complexity ? (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              )}
            </button>

            {expandedSections.complexity && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{complexity.totalScore}</div>
                  <div className="text-xs text-gray-600 mt-1">Total Score</div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{complexity.simpleCount}</div>
                  <div className="text-xs text-gray-600 mt-1">Simple</div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{complexity.moderateCount}</div>
                  <div className="text-xs text-gray-600 mt-1">Moderate</div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{complexity.complexCount}</div>
                  <div className="text-xs text-gray-600 mt-1">Complex</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Architecture View */}
        {activeTab === 'architecture' && (
          <div className="space-y-6">
            {/* Visual Diagram */}
            {viewMode === 'visual' && architecture && (
              <VisualArchitectureDiagram
                architecture={architecture}
                onSystemClick={(systemId) => setSelectedSystem(selectedSystem === systemId ? null : systemId)}
              />
            )}

            {/* Cards View */}
            {viewMode === 'cards' && (
              <>
                {/* Systems Grid */}
                <div>
              <h4 className="font-bold text-gray-900 mb-4">Systems & Components</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {architecture.systems?.map((system) => {
                  const integration = architecture.integrationPoints?.find(ip => ip.system === system.id);
                  const complexityColors = integration ? getComplexityColor(integration.complexity) : null;

                  return (
                    <div
                      key={system.id}
                      onClick={() => setSelectedSystem(selectedSystem === system.id ? null : system.id)}
                      className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedSystem === system.id
                          ? 'border-purple-500 shadow-lg'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${complexityColors?.bg || 'bg-gray-100'}`}>
                          {getSystemIcon(system.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h5 className="font-bold text-gray-900 truncate">{system.name}</h5>
                            {integration && (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${complexityColors?.badge}`}>
                                {integration.complexity}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{system.category}</p>
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">{system.description}</p>
                        </div>
                      </div>

                      {/* Integration Details (when selected) */}
                      {selectedSystem === system.id && integration && (
                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                          <div className="text-xs">
                            <span className="font-semibold text-gray-700">Auth:</span>
                            <span className="ml-2 text-gray-600">{integration.authMethod}</span>
                          </div>
                          <div className="text-xs">
                            <span className="font-semibold text-gray-700">Rate Limit:</span>
                            <span className="ml-2 text-gray-600">{integration.rateLimit}</span>
                          </div>
                          <div className="text-xs">
                            <span className="font-semibold text-gray-700">Error Handling:</span>
                            <span className="ml-2 text-gray-600">{integration.errorHandling}</span>
                          </div>
                          {integration.constraints && integration.constraints.length > 0 && (
                            <div className="text-xs">
                              <span className="font-semibold text-gray-700">Constraints:</span>
                              <ul className="ml-4 mt-1 list-disc text-gray-600">
                                {integration.constraints.map((constraint, idx) => (
                                  <li key={idx}>{constraint}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Connections */}
            <div>
              <h4 className="font-bold text-gray-900 mb-4">Data Connections</h4>
              <div className="space-y-3">
                {architecture.connections?.map((conn) => {
                  const fromSystem = architecture.systems?.find(s => s.id === conn.from);
                  const toSystem = architecture.systems?.find(s => s.id === conn.to);

                  return (
                    <div key={conn.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{fromSystem?.name}</div>
                          <div className="text-xs text-gray-500">{fromSystem?.type}</div>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {conn.protocol}
                          </div>
                          <div className="text-xs text-gray-500">→ {conn.dataType}</div>
                        </div>
                        <div className="flex-1 text-right">
                          <div className="font-semibold text-gray-900">{toSystem?.name}</div>
                          <div className="text-xs text-gray-500">{toSystem?.type}</div>
                        </div>
                      </div>
                      {conn.description && (
                        <p className="text-xs text-gray-600 mt-3 pl-4 border-l-2 border-blue-300">
                          {conn.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Security Boundaries */}
            {architecture.securityBoundaries && architecture.securityBoundaries.length > 0 && (
              <div className="bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-200 rounded-lg p-4">
                <button
                  onClick={() => setExpandedSections(prev => ({ ...prev, security: !prev.security }))}
                  className="w-full flex items-center justify-between"
                >
                  <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Security Boundaries
                  </h4>
                  {expandedSections.security ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </button>

                {expandedSections.security && (
                  <div className="mt-4 space-y-3">
                    {architecture.securityBoundaries.map((boundary) => (
                      <div key={boundary.id} className="bg-white rounded-lg p-3 border border-green-200">
                        <div className="font-semibold text-gray-900">{boundary.name}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Access Control: {boundary.accessControl}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          Systems: {boundary.systems.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* Data Flow View */}
        {activeTab === 'dataflow' && dataFlow && (
          <div className="space-y-6">
            {/* Visual Data Flow Diagram */}
            {viewMode === 'visual' && (
              <VisualDataFlowDiagram dataFlow={dataFlow} />
            )}

            {/* Cards View */}
            {viewMode === 'cards' && (
              <>
                {/* Data Source */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                Data Source (Origin)
              </h4>
              <div className="bg-white rounded-lg p-3">
                <div className="font-semibold text-gray-900">{dataFlow.source.system}</div>
                <p className="text-sm text-gray-600 mt-1">{dataFlow.source.description}</p>
              </div>
            </div>

            {/* Transformations */}
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-200 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Server className="w-5 h-5 text-purple-600" />
                Data in Motion (Transformations)
              </h4>
              <div className="space-y-2">
                {dataFlow.transformations?.map((transform, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border-l-4 border-purple-500">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{transform.step}</div>
                        <div className="text-xs text-gray-500 mt-1">System: {transform.system}</div>
                        <p className="text-sm text-gray-600 mt-1">{transform.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Storage */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-green-600" />
                Data at Rest (Storage)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {dataFlow.storage?.map((store, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-gray-900">{store.system}</div>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        store.type === 'permanent'
                          ? 'bg-green-600 text-white'
                          : 'bg-yellow-600 text-white'
                      }`}>
                        {store.type}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      Retention: {store.retention}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Access Control Points */}
            <div className="bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-600" />
                Access Control Points
              </h4>
              <div className="space-y-2">
                {dataFlow.accessControl?.map((ac, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border-l-4 border-red-500">
                    <div className="font-semibold text-gray-900">{ac.point}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Roles:</span> {ac.roles.join(', ')}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Permissions:</span> {ac.permissions}
                    </div>
                  </div>
                ))}
              </div>
            </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchitectureDiagram;
